// Service for employee list operations
import { apiClient, API_CONFIG } from '../../../core';
import type { ApiResponse } from '../../../shared';
import { buildRequiredQueryString } from '../../../shared';
import type {
  EmployeeListParams,
  EmployeesPage,
  EnableDisableResult,
  LC_EnableDisableEmployeeIn,
  LC_GetEmployeesOut,
} from '../types';
import {
  mapEnableToStatus,
  mapPagedEmployees,
} from '../mappers';


export class EmployeeListService {
  async list(
    params: EmployeeListParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<EmployeesPage>> {
    const queryString = buildRequiredQueryString(
      {
        companyIds: params.companyIds,
        statusFilter: params.statusFilter,
        contractStatusFilter: params.contractStatusFilter ?? 'ALL',
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText,
        orderBy: params.sort?.key,
        orderDirection: params.sort?.direction?.toUpperCase(),
      },
      ['companyIds'],
    );
    const endpoint = `${API_CONFIG.ENDPOINTS.EMPLOYEES.LIST}${queryString}`;
    
    if (import.meta.env.VITE_API_DEBUG === 'true') {
      // eslint-disable-next-line no-console
      console.debug('[employeeListService.list] GET', endpoint);
    }
    
    const response = await apiClient.get<LC_GetEmployeesOut>(endpoint, { signal });
    
    if (!response.success || !response.data) {
      return { ...response, data: null };
    }
    
    try {
      const mapped = mapPagedEmployees(response.data);
      return { ...response, data: mapped };
    } catch {
      return { ...response, data: null, success: false };
    }
  }

  async enableDisable(
    input: LC_EnableDisableEmployeeIn,
  ): Promise<ApiResponse<EnableDisableResult>> {
    const endpoint = API_CONFIG.ENDPOINTS.EMPLOYEES.ENABLE_DISABLE(input.employeeId);
    
    if (import.meta.env.VITE_API_DEBUG === 'true') {
      // eslint-disable-next-line no-console
      console.debug('[employeeService.enableDisable] PUT', endpoint, input);
    }
    
    const response = await apiClient.put<any>(endpoint, { enable: input.enable });
    
    if (!response.success) {
      return { ...response, data: null };
    }
    
    return {
      ...response,
      data: { success: true, updatedStatus: mapEnableToStatus(input.enable) },
    };
  }
}

export const employeeList = new EmployeeListService();
